import { Dialog, DialogType } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CoalescedPod, PodHistoryEntry } from "../../../api/pods.types";
import { ROUTES } from "../../../config/routes";
import styles from './PodHistoryDialog.module.scss';

interface Props {
  pod?: CoalescedPod;
  providerId?: string;
  show: boolean;
  onDismiss: () => void;
}

const locale = Intl.DateTimeFormat()?.resolvedOptions()?.locale ?? 'en-US';
const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeStyle: 'short' });

const PodHistoryDialog = ({pod, providerId, show, onDismiss}: Props) => {
  const dialogLabelId = useId();
  const historyListRef = useRef<HTMLDivElement>(null);
  const [showScrollTip, setShowScrollTip] = useState(false);

  const provider = pod?.providers.find(provider => provider.providerId === providerId);
  const providerName = provider ? `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}` : ''}` : 'this provider';

  /**
   * Iterate the providers list and generate a chronological list of activate/deactivate
   * actions for the selected provider.
   */
  const historySteps: PodHistoryEntry[] = useMemo(() => {
    if (!pod) return [];

    const provider = pod.providers.find(provider => provider.providerId === providerId);
    if (!provider) return [];
    
    const events = provider.history;

    events.sort((a, b) => {
      if (a.date > b.date) return 1;
      if (b.date > a.date) return -1;
      return 0;
    });

    return events;
  }, [pod, providerId]);

  const scrollToLatest = () => {
    if (historyListRef.current) {
      historyListRef.current.scrollTop = historyListRef.current.scrollHeight;

      if (historyListRef.current.scrollHeight > historyListRef.current.clientHeight) {
        setShowScrollTip(true);
      } else {
        setShowScrollTip(false);
      }
    }
  }

  useEffect(() => scrollToLatest(), [historySteps]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (!show) {
      timeout = setTimeout(() => {
        setShowScrollTip(false);
      }, 500);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    }
  }, [show]);
  
  return (
    <Dialog
      hidden={!show}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.close,
        title: 'History',
        closeButtonAriaLabel: 'Close',
      }}
      styles={{
        main: {
          minHeight: 'unset',
        }
      }}
      modalProps={{
        titleAriaId: dialogLabelId,
        isBlocking: false,
        // styles: { main: { maxWidth: 450 } },
        layerProps: {
          onLayerDidMount: () => {
            scrollToLatest();
          }
        }
      }}
    >
      <div className={styles.historyWrapper}>
        <div className={styles.historyList} ref={historyListRef} onScroll={(e) => {
          setShowScrollTip(e.currentTarget.scrollTop > 50);
        }}>
          {historySteps.map((step, i) => {
            const action = step.status === 'DEACTIVATED'
              ? 'removed'
              : (i > 1)
                ? 'readded'
                : 'added';
            
            /**
             * @todo Calculate the provider name based on each entry, so that changes to
             * suffixes are captured in the timeline.
             */
            const description = `${step.actor?.name ?? 'A user'} ${action} ${step.subject ?? providerName}`;
            const richDescription = step.actor?.id
              ? <><Link to={`${ROUTES.accounts}/${step.actor.id}`} className={styles.actorLink}>{step.actor.name}</Link> {action} {step.subject ?? providerName}</>
              : description;
            
            return (
              <div className={`${styles.historyItem}${step.status === 'DEACTIVATED' ? ` ${styles.inactive}` : ''}`} key={i}>
                <div className={styles.historyDot} aria-hidden="true"></div>
                <div className={styles.historyLine} aria-hidden="true"></div>
                <div className={styles.historyDescription}>
                    {richDescription}
                </div>
                <div className={styles.historyDate}>{dateFormat.format(step.date * 1000)}</div>
              </div>
            )}
          )}
        </div>
        <div className={`${styles.historyScrollBox}${!showScrollTip ? ` ${styles.hidden}` : ''}`}>
          <div className={styles.historyScrollCaddy}>
            <div className={styles.historyScrollTip}>Scroll up for earlier</div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default PodHistoryDialog;